import { useNavigate, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  Compass, 
  UtensilsCrossed, 
  Settings, 
  ClipboardList,
  LogOut,
  Menu,
  X,
  Building2,
  Users,
  Info,
  QrCode,
  Clock,
  FileBarChart,
  MessageSquare, BarChart3,
  Ticket, Megaphone
, Shield} from "lucide-react";
import { logoutAdmin, logoutStaff, isAdminAuthenticated, getCurrentUser } from "@/lib/store";
import { removeAuthToken } from "@/lib/api";
import { mascotBase64 } from "@/assets/mascotBase64";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import ErrorBoundary from "@/components/ErrorBoundary";

export const isRouteAllowed = (path, user) => {
  if (!user) return false;
  if (user.role === "admin" || user.role === "global") return true;

  switch (path) {
    case "/admin/dashboard":
      return Boolean(
        user.can_manage_settings ||
        user.can_edit_menu ||
        user.can_manage_experiences ||
        user.can_manage_users ||
        user.can_view_reports ||
        user.can_view_analytics ||
        user.can_manage_discounts ||
        user.can_manage_qrcodes ||
        user.can_view_logs ||
        user.can_edit_messages
      );
    case "/staff/dashboard":
      return true;
    case "/admin/analytics":
      return Boolean(user.can_view_analytics);
    case "/admin/properties":
      return Boolean(user.can_manage_settings);
    case "/admin/experiences":
      return Boolean(user.can_manage_experiences);
    case "/admin/vendors":
      return Boolean(user.can_manage_experiences);
    case "/admin/menu":
      return Boolean(user.can_edit_menu);
    case "/admin/category-settings":
      return Boolean(user.can_manage_settings || user.can_edit_menu);
    case "/admin/logs":
      return Boolean(user.can_view_logs);
    case "/admin/audit-trail":
      return Boolean(user.can_view_logs);
    case "/admin/reports":
      return Boolean(user.can_view_reports);
    case "/admin/users":
      return Boolean(user.can_manage_users);
    case "/admin/property-info":
      return Boolean(user.can_manage_settings);
    case "/admin/qr-codes":
      return Boolean(user.can_manage_qrcodes);
    case "/admin/message-templates":
      return Boolean(user.can_edit_messages);
    case "/admin/broadcast":
      return true; // All managers can broadcast to their own property
    case "/admin/coupons":
      return Boolean(user.can_manage_discounts);
    case "/admin/settings":
      return Boolean(user.can_manage_settings);
    default:
      return false;
  }
};

export const AdminLayout = ({ children, title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const user = getCurrentUser();
  const isAdminOrGlobal = Boolean(user && (user.role === "admin" || user.role === "global"));

  useEffect(() => {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      if (!isAdminAuthenticated()) {
        navigate("/admin");
      }
      return;
    }

    if (currentUser.role !== "admin" && currentUser.role !== "global") {
      const routeAllowed = isRouteAllowed(location.pathname, currentUser);
      // Allow staff-authenticated managers to access routes explicitly marked as allowed for all
      if (!isAdminAuthenticated() && !routeAllowed) {
        navigate(`/staff/dashboard?property=${currentUser.property_id || "varanasi"}`);
        return;
      }
      if (!routeAllowed) {
        toast.error("Access denied: You do not have permission for this section");
        navigate(`/staff/dashboard?property=${currentUser.property_id || "varanasi"}`);
        return;
      }
    }
  }, [navigate, location.pathname]);

  const navItems = [
    { path: "/admin/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/staff/dashboard", icon: ClipboardList, label: "Live Operations" },
    { path: "/admin/broadcast", icon: Megaphone, label: "Broadcast" },
    { path: "/admin/analytics", icon: BarChart3, label: "Analytics" },
    { path: "/admin/properties", icon: Building2, label: "Properties" },
    { path: "/admin/experiences", icon: Compass, label: "Experiences" },
    { path: "/admin/vendors", icon: Users, label: "Vendors" },
    { path: "/admin/menu", icon: UtensilsCrossed, label: "Menu" },
    { path: "/admin/category-settings", icon: Clock, label: "Timings" },
    { path: "/admin/logs", icon: ClipboardList, label: "Logs" },
    { path: "/admin/audit-trail", icon: Shield, label: "Audit Trail" },
    { path: "/admin/reports", icon: FileBarChart, label: "Reports" },
    { path: "/admin/users", icon: Users, label: "Team" },
    { path: "/admin/property-info", icon: Info, label: "Property Info" },
    { path: "/admin/qr-codes", icon: QrCode, label: "QR Codes" },
    { path: "/admin/message-templates", icon: MessageSquare, label: "Messages" },
    { path: "/admin/coupons", icon: Ticket, label: "Coupons" },
    { path: "/admin/settings", icon: Settings, label: "Settings" },
  ];

  const visibleNavItems = navItems.filter((item) => {
    if (isAdminOrGlobal) return true;
    return isRouteAllowed(item.path, user);
  });

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    const user = getCurrentUser();
    const isStaffOrManager = user?.role === "manager" || user?.role === "kitchen" || user?.role === "staff";
    const propId = user?.property_id;
    logoutAdmin();
    logoutStaff();
    removeAuthToken();
    localStorage.removeItem("hm_user");
    localStorage.removeItem("hm_impersonator");
    if (isStaffOrManager) {
      navigate(propId ? `/team/login?property=${propId}` : "/team/login");
    } else {
      navigate("/admin");
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F6] flex">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="fixed top-4 left-4 z-50 p-2 rounded-xl bg-[#2A9D8F] text-white md:hidden shadow-lg"
        data-testid="admin-mobile-menu"
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Sidebar */}
      <aside 
        className={`admin-sidebar fixed md:sticky md:top-0 inset-y-0 left-0 z-40 h-screen overflow-y-auto flex flex-col transform ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0 transition-transform duration-300`}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="mb-6 pt-2 flex flex-col items-center text-center">
          <img 
            src={mascotBase64} 
            alt="Hidden Monkey" 
            className="w-20 h-20 object-contain mascot-float drop-shadow-lg"
          />
          <h1 className="font-accent text-white text-xl font-extrabold mt-2.5 tracking-tight drop-shadow-sm">
            Koko
          </h1>
          <span className="text-emerald-100/95 text-[11px] font-semibold tracking-widest uppercase bg-black/15 px-3 py-0.5 rounded-full mt-1 border border-white/10">
            Guest Operations Hub
          </span>
        </div>

        {/* Navigation */}
        <nav className="flex flex-col gap-1">
          {visibleNavItems.map((item) => {
            const targetPath = item.path === "/staff/dashboard"
              ? `/staff/dashboard?property=${user?.property_id || "varanasi"}`
              : item.path;
            const isItemActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => {
                  navigate(targetPath);
                  setSidebarOpen(false);
                }}
                className={`admin-nav-item ${isItemActive ? "active" : ""}`}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Logout & Navigation */}
        <div className="mt-auto pt-8 flex flex-col gap-2">
          <button
            onClick={() => navigate(`/staff/dashboard?property=${user?.property_id || "varanasi"}`)}
            className="admin-nav-item w-full text-white/90 bg-white/10 hover:bg-white/20"
            data-testid="switch-team-dashboard"
          >
            <LayoutDashboard className="w-5 h-5 text-[#E9C46A]" />
            <span>Live Operations</span>
          </button>
          <button
            onClick={handleLogout}
            className="admin-nav-item w-full text-white/70 hover:text-[#FFC107]"
            data-testid="admin-logout"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 p-4 md:p-8 ml-0 md:ml-0 pt-16 md:pt-8 min-w-0 overflow-x-hidden">
        <div className="max-w-6xl mx-auto">
          {title && (
            <h1 className="font-accent text-2xl font-bold text-[#2A9D8F] mb-6">{title}</h1>
          )}
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
